import React from "react";
import Section from "./Section";
import img1 from "../../img/25361.jpg";

export default function HTML_Images() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🖼️ HTML Images</h1>

      {/* 🔹 Introduction */}
      <p className="tutorial-intro">
        HTML images are added using the{" "}
        <code className="highlight_code_1">&lt;img&gt;</code> tag. Images make
        webpages more engaging and informative. You can display, resize, and
        align them with attributes like{" "}
        <code className="highlight_code_1">src</code>,{" "}
        <code className="highlight_code_1">alt</code>,{" "}
        <code className="highlight_code_1">width</code>, and{" "}
        <code className="highlight_code_1">height</code>.
      </p>

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
          borderRadius: "12px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <h3
          style={{
            color: "#0284c7",
            fontWeight: 700,
            fontSize: "1.5rem",
            marginBottom: "0.5rem",
          }}
        >
          📜 Common Image Attributes
        </h3>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code_1">src</code> — Specifies the image
            file path or URL.
          </li>
          <li>
            <code className="highlight_code_1">alt</code> — Provides alternate
            text if the image cannot be displayed.
          </li>
          <li>
            <code className="highlight_code_1">width</code> &{" "}
            <code className="highlight_code_1">height</code> — Define image
            dimensions.
          </li>
          <li>
            <code className="highlight_code_1">style</code> — Inline CSS for
            styling and layout.
          </li>
        </ul>
      </div>

      {/* Section 1️⃣ — Basic Image Example */}
      <Section
        title="1️⃣ Basic Image Example"
        html={`<img src="${img1}"  alt="Placeholder Image" width=100% />`}
        description="The <img> tag embeds an image on the webpage using the src and alt attributes."
      />

      {/* Section 2️⃣ — Local Image Example */}
      <Section
        title="2️⃣ Using Local Images (from project folder)"
        html={`<img src="${img1}" alt="Local Image Example" width="200" />`}
        description="You can also use local images stored in your project folder, imported as shown in React."
      />

      {/* Section 3️⃣ — Image with Width & Height */}
      <Section
        title="3️⃣ Image with Width and Height"
        html={`<img src="${img1}" alt="Resized Image" width="300" height="150" />`}
        description="You can resize images using width and height attributes, or control size with CSS."
      />

      {/* Section 4️⃣ — Image with Style Attribute */}
      <Section
        title="4️⃣ Styling an Image"
        html={`<img src="${img1}" width=100% alt="Styled Image" style="border-radius: 12px; border: 2px solid #0284c7;" />`}
        description="You can add inline CSS to style your image — like rounded corners, borders, or shadows."
      />

      {/* Section 5️⃣ — Image as a Link */}
      <Section
        title="5️⃣ Image as a Link"
        html={`<a href="https://www.wikipedia.org"  target="_blank">
  <img src="${img1}" alt="Wikipedia" width=100% />
</a>`}
        description="You can wrap an image inside an <a> tag to make it clickable, just like a hyperlink."
      />

      {/* Section 6️⃣ — Floating (Left & Right Aligned) */}
      <Section
        title="6️⃣ Aligning Images (Float)"
        html={`<p>
  <img src="${img1}" alt="Wikipedia" width=20%  alt="Left" style="float:left; margin-right:10px;" />
    This image floats to the left of the text.
</p>
<p style="clear:both;">
  <img src="${img1}" alt="Wikipedia" width=20%  alt="Right" style="float:right; margin-left:10px;" />
  This image floats to the right of the text.
</p>`}
        description="Images can be aligned beside text using the CSS float property (left or right)."
      />

      {/* Section 7️⃣ — Background Image (via CSS) */}
      <Section
        title="7️⃣ Using Background Images (CSS)"
        html={`<div style="background-image: url(${img1}); height: 100px; width: 300px; background-size: cover; color:white; display:flex; align-items:center; justify-content:center;">
  Background Image Example
</div>`}
        description="You can use CSS background-image to set an image as the background of a div or section."
      />

      {/* Section 8️⃣ — Responsive Image */}
      <Section
        title="8️⃣ Responsive Image Example"
        html={`<img src="${img1}" 
  alt="Responsive Example" 
  style="max-width:100%;
  height:auto;" />`}
        description="Responsive images adjust automatically to fit different screen sizes using CSS."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — HTML Images</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code className="highlight_code_1">&lt;img&gt;</code> with{" "}
            <code className="highlight_code_1">src</code> and{" "}
            <code className="highlight_code_1">alt</code> attributes to display
            images.
          </li>
          <li>You can use local or external image sources (URLs).</li>
          <li>
            Control image size using{" "}
            <code className="highlight_code_1">width</code>,{" "}
            <code className="highlight_code_1">height</code>, or CSS.
          </li>
          <li>
            Use <code className="highlight_code_1">float</code> or{" "}
            <code className="highlight_code_1">flexbox</code> for layout
            alignment.
          </li>
          <li>
            Always include <code className="highlight_code_1">alt</code> text
            for accessibility and SEO.
          </li>
        </ul>
      </div>
    </div>
  );
}
